import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AcceptInvite from './pages/AcceptInvite';
import SuperAdmin from './pages/SuperAdmin';
import Module19Preview from './pages/Module19Preview';
import HelpManual from './pages/HelpManual';
import { useAuth } from './hooks/useAuth';
import { impersonateTenant, restoreImpersonation } from './services/api';
import { TimezoneProvider } from './context/TimezoneContext';

export default function App() {
  const { user, loading, login, logout } = useAuth();

  // Swap the current super admin session for a tenant-scoped one. The backend
  // keeps the original super admin ID inside the impersonation JWT so it can be
  // restored without storing any tokens in browser storage.
  async function handleImpersonate(tenantId: string) {
    try {
      const res = await impersonateTenant(tenantId);
      sessionStorage.setItem('impersonator_user', JSON.stringify(user));
      login(res.user);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to impersonate tenant');
    }
  }

  async function handleEndImpersonation() {
    sessionStorage.removeItem('impersonator_user');
    try {
      const restoredUser = await restoreImpersonation();
      login(restoredUser);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to return to super admin session');
    }
  }

  if (loading) {
    return (
      <div className="sd-app-loading" role="status" aria-live="polite">
        <div className="sd-app-loading-mark" aria-hidden="true">
          <span />
          <img src="/brand/seedronics-wordmark-light.png" alt="" />
        </div>
        <p>Preparing command center</p>
        <div className="sd-app-loading-bar" aria-hidden="true">
          <span />
        </div>
      </div>
    );
  }

  return (
    <TimezoneProvider>
      <Routes>
        <Route path="/module19-preview" element={<Module19Preview />} />
        <Route path="/use-cases" element={<Module19Preview authMode="routes" initialSection="use-cases" />} />
        <Route path="/pricing" element={<Module19Preview authMode="routes" initialSection="pricing" />} />
        <Route path="/faq" element={<Module19Preview authMode="routes" initialSection="faq" />} />
        <Route path="/contact" element={<Module19Preview authMode="routes" initialSection="contact" />} />
        <Route path="/help/manual" element={<HelpManual />} />
        <Route
          path="/"
          element={
            user ? <Navigate to="/dashboard" replace /> : <Module19Preview authMode="routes" />
          }
        />
        <Route
          path="/login"
          element={
            user ? <Navigate to="/dashboard" replace /> : <Login onLogin={login} />
          }
        />
        <Route
          path="/dashboard"
          element={
            user ? (
              user.role === 'super_admin' ? <Navigate to="/super-admin" replace /> : <Dashboard user={user} onLogout={logout} onEndImpersonation={handleEndImpersonation} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/super-admin"
          element={
            user ? (
              user.role === 'super_admin' ? <SuperAdmin user={user} onLogout={logout} onImpersonate={handleImpersonate} /> : <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        {/* Module 19A public identity routes */}
        <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <Signup onSignup={login} />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    </TimezoneProvider>
  );
}
